import React, { useEffect, useMemo, useState } from "react"
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
} from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import TableContainerV1 from "components/Common/TableContainerV1"
import ToastDialog from "components/Common/ToastDialog"
import { getPaymentTermsList } from "helpers/UAM/paymentTerm.helper"
import { getMaster } from "http/master/master"
import { AiOutlineSync } from 'react-icons/ai';
import { getApiLogs } from "http/apiLogs/apiLogs"
import { formatDateTime ,isPermission} from "common/utility"
import FullPageLoader from "components/Common/FullPageLoader"

const PaymentTermsList = () => {
  document.title = "Payment Terms List | DMS"
  const [paymentTermsList, setPaymentTermsList] = useState([])
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageCount: 0,
    totalRecords: 0,
  })
  const [pageSize, setPageSize] = useState(10)
  const [searchValue, setSearchValue] = useState("")
  const [columnId, setColumnId] = useState("id")
  const [sortingOrderStatus, setSortingOrderStatus] = useState("desc")
  const [sortingOptions, setSortingOptions] = useState({
    column: "id",
    isAsc: false,
    optionalColumn: ["Action"],
  })
  const [toastInfo, setToastInfo] = useState({ showToast: false })
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSynced, setLastSynced] = useState(null);
  const [syncPermission, setSyncPermission] = useState(false);

  const columns = useMemo(
    () => [
      {
        Header: "Payment ID",
        accessor: "paymentId",
        filterable: true,
        Cell: cellProps => {
          return cellProps.value ? cellProps.value : '';
        },
      },
      {
        Header: "Description",
        accessor: "description",
        filterable: true,
        Cell: cellProps => {
          return cellProps.value ? cellProps.value : '';
        },
      },
    ],
    []
  )

  const getPaymentTermsListHelper = data => {
    getPaymentTermsList(data)
      .then(responseFromAPI => {
        if (responseFromAPI.status && responseFromAPI.code == 200) {
          setPaymentTermsList(responseFromAPI.data.data)
          const { totalItems, totalPages, currentPage } = responseFromAPI.data
          setPagination({
            pageIndex: currentPage,
            pageCount: totalPages,
            totalRecords: totalItems,
          })
        } else {
          setPagination({ pageIndex: 0, pageCount: 0, totalRecords: 0 })
          setPaymentTermsList([])
        }
      })
      .catch(error => {
        console.error(error)
      })
  }

  const handlePageChange = (
    page,
    pageSize,
    searchKey=searchValue,
    columnName = columnId,
    sortingOrder = sortingOrderStatus
  ) => {
    setPageSize(pageSize)
    getPaymentTermsListHelper({
      page,
      limit: pageSize,
      searchKey,
      columnName,
      sortingOrder,
    })
  }

  const handleSortColumn = option => {
    setSortingOptions(option)
    const columnName = option.column
    setColumnId(columnName)
    const sortingOrder = option.isAsc ? "asc" : "desc"
    setSortingOrderStatus(sortingOrder)
    handlePageChange(1, pageSize, searchValue, columnName, sortingOrder)
  }

  const handleSearchFilter = (value, pageSize) => {
    setSearchValue(value)
    handlePageChange(1, pageSize, value)
  }
  const handleClearSearch = () => {
    setSearchValue("")
    handlePageChange(1, pageSize, "")
  }

  useEffect(() => {
    getPaymentTermsListHelper({})
    fetchLastSynced();
    checkPermission()
  }, [])

  const checkPermission = async () => {
    let isSyncPermission = await isPermission('sync', 'create_permission')
    setSyncPermission(isSyncPermission)
  }

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      let res = await getMaster({ masterName: 'payment_terms' })
      if (res.status) {
        setToastInfo({
          message: res.message,
          showToast: true,
          role: "success",
        })
        getPaymentTermsListHelper({})
        setIsSyncing(false);
        fetchLastSynced();
      }
    } catch (err) {
      setToastInfo({
        message: "Something went wrong",
        showToast: true,
        role: "error",
      })
      setIsSyncing(false);
    }
  }

  const fetchLastSynced = async () => {
    try {
      let res = await getApiLogs({ masterName: 'payment_terms_master' })
      if (res.status) {
        setLastSynced(formatDateTime(res.data.initialDateTime))
      }
    } catch (err) {
      console.error(err)
    }
  }
  const buttonSection = (
    <>
      {syncPermission && (<div>
        <div className="d-flex justify-content-end mb-1">
          <button
            className="btn btn-success"
            type="button"
            onClick={handleSync}
          >
            <AiOutlineSync size={15} className={isSyncing ? 'rotating' : ''} /> Sync Data
          </button>
        </div>
        {lastSynced && <p className="d-flex justify-content-end ">Last Synced: {lastSynced}</p>}
      </div>)}
    </>

  )

  return (
    <div className="page-content">
      {isSyncing && <FullPageLoader />}
      <div className="ms-2">
        <Breadcrumbs title="Payment Terms" breadcrumbItem="Payment Terms List" />
      </div>
      <Container fluid>
        <Row className="vertical-scroll">
          <Col xs="12">
            <Card>
              <CardBody>
                <TableContainerV1
                  columns={columns}
                  data={paymentTermsList}
                  pagination={pagination}
                  handlePageChange={handlePageChange}
                  searchHelperText="Search by Payment ID"
                  handleSearchFilter={handleSearchFilter}
                  sortOptions={sortingOptions}
                  customSection={buttonSection}
                  handleSortColumn={handleSortColumn}
                  handleClearSearch={handleClearSearch}
                  className="custom-header-css"
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <ToastDialog
        message={toastInfo.message}
        showToast={toastInfo.showToast}
        role={toastInfo.role}
        onCloseFn={() => setToastInfo({ showToast: false })}
      />
    </div>
  )
}

export default PaymentTermsList
